type PrimitiveValue = string | number | boolean | null;

/**
 * Converts a PascalCase or camelCase string to kebab-case.
 *
 * @param text - The text to convert
 * @returns The kebab-case version of the text
 */
export function toKebabCase(text: string): string {
  return text
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

/**
 * Checks if a value is a primitive that can be converted to a CSS variable.
 *
 * @param value - The value to check
 * @returns True if the value is a primitive
 */
function isPrimitive(value: unknown): value is PrimitiveValue {
  if (value === null) {
    return true;
  }

  const valueType = typeof value;

  return valueType === 'string' || valueType === 'number' || valueType === 'boolean';
}

/**
 * Checks if a value is a styled-components css template literal result.
 * Styled-components css template literals are actually arrays with special properties.
 *
 * @param value - The value to check
 * @returns True if the value is a styled-components css object
 */
function isStyledComponentsCss(value: unknown): boolean {
  if (!Array.isArray(value)) {
    return false;
  }

  // Styled-components css arrays contain CSS strings
  // They typically have 1 element that is a multi-line CSS string
  if (value.length === 0) {
    return false;
  }

  const firstItem = value[0];

  // Check if the first item is a string that looks like CSS (contains colons, semicolons, or newlines)
  if (typeof firstItem === 'string') {
    const trimmed = firstItem.trim();

    // CSS strings typically contain property declarations with colons
    return trimmed.includes(':') && (trimmed.includes(';') || trimmed.includes('\n'));
  }

  return false;
}

/**
 * Checks if a value is a plain object (not an array, Date, styled-components css, etc.).
 * Also accepts ES6 Module objects from theme exports.
 *
 * @param value - The value to check
 * @returns True if the value is a plain object or ES6 module
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  if (Array.isArray(value) || isStyledComponentsCss(value)) {
    return false;
  }

  const stringTag = Object.prototype.toString.call(value);

  // Accept both plain objects and ES6 Module objects
  return stringTag === '[object Object]' || stringTag === '[object Module]';
}

/**
 * Recursively flattens a nested theme object into CSS custom properties.
 *
 * Automatically filters out:
 * - Functions and symbols
 * - Null and undefined values
 * - Numeric keys (TypeScript enum reverse mappings)
 * - Styled-components css template literals
 * - Non-plain objects (Date, RegExp, etc.)
 *
 * @param themeObject - The theme object to flatten
 * @param prefix - The current prefix for nested properties
 * @returns A flat object with CSS variable names as keys
 */
function flattenThemeObject(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  themeObject: Record<string, any>,
  prefix = ''
): Record<string, string> {
  const result: Record<string, string> = {};

  // Get all own property names (including non-enumerable ones for ES6 modules)
  const keys = Object.getOwnPropertyNames(themeObject);

  for (const key of keys) {
    // Skip __proto__, constructor, and other special properties
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue;
    }

    const value = themeObject[key];

    // Skip functions and symbols
    if (typeof value === 'function' || typeof value === 'symbol') {
      continue;
    }

    // Skip numeric keys (these are reverse mappings from TypeScript enums)
    if (!isNaN(Number(key))) {
      continue;
    }

    // Build the CSS variable name
    const kebabKey = toKebabCase(key);
    const variableName = prefix ? `${prefix}-${kebabKey}` : kebabKey;

    if (isPrimitive(value)) {
      // Convert primitive values to strings
      if (value !== null) {
        result[`--theme-${variableName}`] = String(value);
      }
    } else if (isStyledComponentsCss(value)) {
      // Skip styled-components css template literals (they are arrays but should be ignored)
      continue;
    } else if (Array.isArray(value)) {
      // Convert arrays to space-separated strings (for CSS shorthand like padding, margin)
      const arrayValues = value
        .filter((item) => isPrimitive(item) && item !== null)
        .map((item) => {
          const stringValue = String(item);

          // Add 'px' unit to numeric values (not booleans or already-string values)
          if (typeof item === 'number') {
            return `${stringValue}px`;
          }

          return stringValue;
        });

      if (arrayValues.length > 0) {
        result[`--theme-${variableName}`] = arrayValues.join(' ');
      }
    } else if (isPlainObject(value)) {
      // Recursively flatten nested objects
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nested = flattenThemeObject(value as Record<string, any>, variableName);

      Object.assign(result, nested);
    }
    // Note: Any other value types (non-plain objects, etc.) are silently skipped
  }

  return result;
}

/**
 * Converts a theme object to a flat object of CSS custom properties.
 *
 * @param theme - The theme object to convert
 * @returns A flat object with CSS variable names as keys and values as strings
 */
export function themeToFlatCssVars(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: Record<string, any>
): Record<string, string> {
  return flattenThemeObject(theme);
}

/**
 * Converts a flat CSS variables object to a CSS string for injection.
 *
 * @param cssVars - The CSS variables object
 * @returns A CSS string with all variables
 */
export function cssVarsToString(cssVars: Record<string, string>): string {
  return Object.entries(cssVars)
    .map(([key, value]) => {
      // Check if value needs quotes (contains spaces, commas, or special characters)
      const needsQuotes = /[\s,]/.test(value);
      const formattedValue = needsQuotes ? `"${value}"` : value;

      return `  ${key}: ${formattedValue};`;
    })
    .join('\n');
}
